// Cor, nome e iniciais determinísticos por pessoa, para distinguir responsáveis do quadro.
// Os membros do quadro são registrados via setBoardIdentities (chamado em Projects.js); com isso
// homônimos ganham identificação (display_label) e iniciais mais longas quando colidem.
const AVATAR_COLORS = [
  'bg-blue-600', 'bg-purple-600', 'bg-emerald-600', 'bg-orange-500', 'bg-pink-600',
  'bg-teal-600', 'bg-indigo-600', 'bg-rose-600', 'bg-amber-600', 'bg-cyan-600',
  'bg-lime-600', 'bg-fuchsia-600',
];

let registry = new Map(); // user_id -> { display, initials }
let nameIndex = new Map(); // nome normalizado -> user_id (só nomes não ambíguos)
let registrySignature = '';
const nameKey = (name) => String(name || '').trim().split(/\s+/).join(' ').toLowerCase();
// Resolve a pessoa pelo id; se a tela não tiver o id, tenta pelo nome do membro
const resolveId = (name, id) => (registry.has(id) ? id : nameIndex.get(nameKey(name)));

// Ignora pontuação solta (ex.: "(Financeiro)" -> "Financeiro")
const words = (name) => String(name || '').split(/\s+/)
  .map(w => w.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, ''))
  .filter(Boolean);

// level 0: inicial do primeiro + inicial do último nome ("PH");
// level 1+: puxa mais uma letra, alternando primeiro nome e último ("PEH", "PEHE", "PEDHE"...).
const candidateInitials = (name, level = 0) => {
  const parts = words(name);
  if (parts.length === 0) return 'U';
  const first = parts[0];
  const last = parts.length > 1 ? parts[parts.length - 1] : '';
  if (!last) return first.slice(0, 2 + level).toUpperCase();
  const firstLen = 1 + Math.ceil(level / 2);
  const lastLen = 1 + Math.floor(level / 2);
  return (first.slice(0, firstLen) + last.slice(0, lastLen)).toUpperCase();
};

export const setBoardIdentities = (members) => {
  const list = (members || [])
    .filter(m => !m.status || m.status === 'active')
    .map(m => ({
      id: m.user_id || m.id,
      names: [m.user_name, m.name, m.user_email].filter(Boolean),
      display: (m.display_label || '').trim() || m.user_name || m.name || m.user_email || '',
    }))
    .filter(m => m.id);
  const signature = list.map(m => `${m.id}:${m.display}`).sort().join('|');
  if (signature === registrySignature) return;
  registrySignature = signature;

  // Nomes idênticos (sem identificação) compartilham as mesmas iniciais; a disputa é entre nomes distintos
  const byDisplay = new Map();
  list.forEach(m => {
    const key = m.display.trim().split(/\s+/).join(' ').toLowerCase();
    byDisplay.set(key, [...(byDisplay.get(key) || []), m]);
  });
  const initialsOf = new Map();
  let pending = [...byDisplay.keys()];
  for (let level = 0; level < 12 && pending.length; level++) {
    const groups = new Map();
    pending.forEach(key => {
      const c = candidateInitials(byDisplay.get(key)[0].display, level);
      groups.set(c, [...(groups.get(c) || []), key]);
    });
    const stillPending = [];
    groups.forEach((keys, c) => {
      if (keys.length === 1) initialsOf.set(keys[0], c);
      else stillPending.push(...keys);
    });
    pending = stillPending;
  }
  pending.forEach(key => initialsOf.set(key, candidateInitials(byDisplay.get(key)[0].display, 0)));

  const next = new Map();
  byDisplay.forEach((people, key) => people.forEach(p => next.set(p.id, { display: p.display, initials: initialsOf.get(key) })));
  registry = next;

  const seen = new Map();
  list.forEach(m => m.names.forEach(n => {
    const k = nameKey(n);
    seen.set(k, seen.has(k) && seen.get(k) !== m.id ? null : m.id);
  }));
  nameIndex = new Map([...seen].filter(([, v]) => v));
};

export const displayName = (name, id) => registry.get(resolveId(name, id))?.display || name || '';

export const avatarColor = (name, id) => {
  const resolved = resolveId(name, id);
  const s = String((resolved || name) || '').trim().toLowerCase();
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

export const avatarInitials = (name, id) => registry.get(resolveId(name, id))?.initials || candidateInitials(name, 0);

// Grupos de membros com o mesmo nome efetivo (sem diferenciar maiúsculas/espaços)
export const findHomonymGroups = (people) => {
  const groups = new Map();
  (people || []).forEach(p => {
    const key = String(p.name || '').trim().split(/\s+/).join(' ').toLowerCase();
    if (!key) return;
    groups.set(key, [...(groups.get(key) || []), p]);
  });
  return [...groups.values()].filter(g => g.length > 1);
};
